// src/components/social/NotificationItem.jsx
import React, { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ReactTimeAgo from 'react-time-ago';
import { 
  FaHeart, 
  FaComment, 
  FaUser, 
  FaAt, 
  FaShare, 
  FaBell, 
  FaEnvelope,
  FaTimes
} from 'react-icons/fa';

import { NotificationContext } from '../../context/notificationContext';
import Avatar from '../ui/Avatar';

const NotificationItem = ({ notification }) => {
  const { markAsRead, deleteNotification } = useContext(NotificationContext);
  const navigate = useNavigate();
  
  // Handle click on notification
  const handleClick = () => {
    if (!notification.read) {
      markAsRead(notification._id);
    }
    
    // Navigate based on notification type
    switch (notification.type) {
      case 'like':
        if (notification.referenceType === 'post') {
          navigate(`/posts/${notification.referenceId}`);
        }
        break;
      case 'comment':
        navigate(`/posts/${notification.referenceId}`);
        break;
      case 'follow':
        navigate(`/profile/${notification.sender._id}`);
        break;
      case 'mention':
        if (notification.referenceType === 'post') {
          navigate(`/posts/${notification.referenceId}`);
        }
        break;
      case 'message':
        navigate(`/messages/${notification.sender._id}`);
        break;
      case 'share':
        navigate(`/posts/${notification.referenceId}`);
        break;
      default:
        // For system notifications or unknown types
        break;
    }
  };

  // Handle notification deletion
  const handleDelete = (e) => {
    e.stopPropagation(); // Prevent triggering handleClick
    deleteNotification(notification._id);
  };

  // Determine icon based on notification type
  const renderIcon = () => {
    const iconSize = 14;
    const iconClasses = "flex items-center justify-center w-8 h-8 rounded-full text-white";
    
    switch (notification.type) {
      case 'like':
        return (
          <div className={`${iconClasses} bg-red-500`}>
            <FaHeart size={iconSize} />
          </div>
        );
      case 'comment':
        return (
          <div className={`${iconClasses} bg-blue-500`}>
            <FaComment size={iconSize} />
          </div>
        );
      case 'follow':
        return (
          <div className={`${iconClasses} bg-green-500`}>
            <FaUser size={iconSize} />
          </div>
        );
      case 'mention':
        return (
          <div className={`${iconClasses} bg-purple-500`}>
            <FaAt size={iconSize} />
          </div>
        );
      case 'message':
        return (
          <div className={`${iconClasses} bg-indigo-500`}>
            <FaEnvelope size={iconSize} />
          </div>
        );
      case 'share':
        return (
          <div className={`${iconClasses} bg-yellow-500`}>
            <FaShare size={iconSize} />
          </div>
        );
      case 'system':
      default:
        return (
          <div className={`${iconClasses} bg-gray-500`}>
            <FaBell size={iconSize} />
          </div>
        );
    }
  };

  return (
    <div 
      className={`px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-start gap-3 ${
        !notification.read ? 'bg-blue-50' : ''
      }`}
      onClick={handleClick}
    >
      {/* Notification icon or avatar */}
      <div className="flex-shrink-0">
        {notification.sender ? (
          <Avatar 
            user={notification.sender} 
            size="sm" 
            linkToProfile={false} 
            className="w-10 h-10 rounded-full"
          />
        ) : (
          renderIcon()
        )}
      </div>
      
      {/* Notification content */}
      <div className="flex-1 min-w-0">
        <div className="text-sm">
          {notification.sender && (
            <span className="font-medium text-gray-900">
              {notification.sender.name}
            </span>
          )}{' '}
          <span className="text-gray-700">{notification.message}</span>
        </div>
        
        <div className="text-xs text-gray-500 mt-1">
          <ReactTimeAgo date={new Date(notification.createdAt)} locale="en-US" timeStyle="twitter" />
        </div>
      </div>
      
      {/* Delete button */}
      <button 
        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full flex-shrink-0"
        onClick={handleDelete}
        aria-label="Delete notification"
      >
        <FaTimes size={14} />
      </button>
      
      {/* Unread indicator */}
      {!notification.read && (
        <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-primary"></div>
      )}
    </div>
  );
};

export default NotificationItem;